import React, { useState, useEffect } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import { Card, CardBody, Table, Button, Input } from "reactstrap"
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa"
import ProductModal from "./ProductModal"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"

const ProductIndex = () => {
  document.title = "Products | Milano Jewelry"

  const { checkAccess } = usePermissionData()

  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState("add")
  const [formData, setFormData] = useState({})

  /* ================= DUMMY DATA ================= */
  useEffect(() => {
    fetchProducts()
  }, [])

  async function fetchProducts() {
    const APIdata = await fetchData(setLoading, "products")
    if (APIdata?.success) {
      setProducts(APIdata?.data?.reverse())
    }
  }

  /* ================= SEARCH ================= */

  const filteredData = products.filter(product => {
    const term = search.toLowerCase()
    return [
      product?.skuNumber,
      product?.productName,
      product?.quantity,
      product?.department?.departmentName,
      product?.category?.categoryName,
      product?.class?.className,
      product?.programBuild?.programBuildName,
      product?.stone?.stoneName,
    ]
      .filter(Boolean)
      .some(val => String(val).toLowerCase().includes(term))
  })

  /* ================= PAGINATION ================= */

  const totalPages = Math.ceil(filteredData.length / pageSize)

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  /* ================= CRUD HANDLERS ================= */

  const openAdd = () => {
    setFormData({})
    setModalMode("add")
    setModalOpen(true)
  }

  const openEdit = item => {
    setFormData(item)
    setModalMode("edit")
    setModalOpen(true)
  }

  const openView = item => {
    setFormData(item)
    setModalMode("view")
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    if (formData?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "products",
        formData?.id,
        formData
      )
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Product Updated Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          fetchProducts()
        }, 1000)
      } else {
        Swal.fire({
          title: "Error Updating Product",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "products", formData)
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Product Added Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          fetchProducts()
        }, 1000)
      } else {
        Swal.fire({
          title: "Error Adding Product",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = async item => {
    const confirm = await Swal.fire({
      title: "Are you Sure?",
      text: `you want to Delete this ${item?.productName} Product`,
      icon: "warning",
      showCancelButton: true,
    })

    if (!confirm.isConfirmed) {
      return
    }
    const APIdata = await FetchDeleteData(setLoading, "products", item?.id)
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Product Deleted Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setTimeout(() => {
        fetchProducts()
      }, 1000)
    } else {
      Swal.fire({
        title: "Error Deleting Product",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Products" />

      <Card>
        <CardBody>
          {/* ===== TOP BAR ===== */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-3 align-items-center">
              <select
                className="form-select"
                style={{ width: 100 }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                {[10, 20, 50].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Search products..."
                style={{ width: "300px" }}
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            {checkAccess(2, "add") && (
              <Button className="btn-blue" onClick={openAdd}>
                <FaPlus className="me-1" />
                Add Product
              </Button>
            )}
          </div>

          {/* ===== TABLE ===== */}
          <Table striped responsive>
            <thead className="table-blue">
              <tr>
                <th>#</th>
                <th>SKU</th>
                <th>Name / Description</th>
                <th>Quantity</th>
                <th>Department</th>
                <th>Category</th>
                <th>Class</th>
                <th>Program Build</th>
                <th>Stone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center">
                    No Products Found
                  </td>
                </tr>
              )}

              {paginatedData.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>{item?.skuNumber || "--"}</td>
                  <td>{item?.productName || "--"}</td>
                  <td>{item?.quantity || "--"}</td>
                  <td>{item?.department?.departmentName || "--"}</td>
                  <td>{item?.category?.categoryName || "--"}</td>
                  <td>{item?.class?.className || "--"}</td>
                  <td>{item?.programBuild?.programBuildName || "--"}</td>
                  <td>{item?.stone?.stoneName || "--"}</td>
                  <td className="d-flex gap-2">
                    <Button color="secondary" onClick={() => openView(item)}>
                      <FaEye />
                    </Button>
                    {checkAccess(2, "edit") && (
                      <Button color="success" onClick={() => openEdit(item)}>
                        <FaEdit />
                      </Button>
                    )}
                    {checkAccess(2, "delete") && (
                      <Button
                        color="danger"
                        onClick={() => handleDelete(item)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ===== PAGINATION ===== */}
          <div className="d-flex justify-content-between">
            <span className="align-self-center">
              Page {currentPage} of {totalPages || 1}
            </span>
            {/* ===== PAGINATION ===== */}
            <div className="d-flex justify-content-center align-items-center gap-2 ">
              {/* Previous */}
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="btn-blue"
              >
                <FaChevronLeft />
              </Button>

              {/* Page Numbers (Show 5 at a time) */}
              {(() => {
                const pageNumbers = []
                const maxVisiblePages = 5

                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxVisiblePages / 2)
                )

                let endPage = startPage + maxVisiblePages - 1

                if (endPage > totalPages) {
                  endPage = totalPages
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <Button
                      key={i}
                      color={currentPage === i ? "primary" : "light"}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </Button>
                  )
                }

                return pageNumbers
              })()}

              {/* Next */}
              <Button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="btn-blue"
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ===== MODAL ===== */}
      <ProductModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        mode={modalMode}
      />
    </div>
  )
}

export default ProductIndex
